import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Loader2, Lock, Users, CheckCircle } from 'lucide-react';

const Admin2FA = () => {
  const [autenticado, setAutenticado] = useState(false);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requires2fa, setRequires2fa] = useState(false);
  const [token, setToken] = useState('');
  const [loadingLogin, setLoadingLogin] = useState(false);

  const [perfilLogado, setPerfilLogado] = useState(sessionStorage.getItem('admin2fa_perfil') || '');
  const isAdmin = perfilLogado === 'ADMIN';

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [codigos, setCodigos] = useState([]);
  const [tempoRestante, setTempoRestante] = useState(30);

  // Restaurar sessão
  useEffect(() => {
    const savedToken = sessionStorage.getItem('admin2fa_token');
    const savedPerfil = sessionStorage.getItem('admin2fa_perfil');
    if (savedToken) {
      setToken(savedToken);
      setPerfilLogado(savedPerfil || '');
      setAutenticado(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      loadUsuarios();
    }
  }, []);

  // QR Code
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('tokens');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoadingLogin(true);
    try {
      const payload = { email, senha };
      if (requires2fa && totpCode) payload.totp_code = totpCode;
      
      const res = await api.post('/auth/login', payload);
      
      if (res.data.requires_2fa) {
        setRequires2fa(true);
        toast.info('Digite o codigo do Google Authenticator');
        setLoadingLogin(false);
        return;
      }
      
      if (res.data.access_token) {
        const perfil = res.data.user?.perfil?.toUpperCase();
        const PERFIS_PERMITIDOS = ['ADMIN', 'SUPERVISOR', 'GERENTE'];
        if (!PERFIS_PERMITIDOS.includes(perfil)) {
          toast.error('Acesso permitido apenas para ADMIN, SUPERVISOR e GERENTE');
          setLoadingLogin(false);
          return;
        }
        setToken(res.data.access_token);
        setAutenticado(true);
        sessionStorage.setItem('admin2fa_token', res.data.access_token);
        sessionStorage.setItem('admin2fa_perfil', perfil);
        setPerfilLogado(perfil);
        api.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
        loadUsuarios();
      } else {
        toast.error('Erro no login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciais invalidas');
    } finally {
      setLoadingLogin(false);
    }
  };

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const savedPerfil = sessionStorage.getItem('admin2fa_perfil') || perfilLogado;
      if (savedPerfil === 'ADMIN') {
        const res = await api.get('/users');
        setUsuarios(res.data.filter(u => u.perfil !== 'ADMIN' && u.ativo !== false));
      } else {
        // Supervisor/Gerente - usar endpoint menos restritivo
        const res = await api.get('/users/chat');
        setUsuarios(res.data.filter(u => u.perfil !== 'ADMIN' && u.ativo !== false));
      }
      loadCodigos();
    } catch {
      toast.error('Erro ao carregar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadCodigos = async () => {
    try {
      const res = await api.get('/auth/2fa/codigos');
      setCodigos(res.data);
    } catch {}
  };

  // Auto-refresh códigos a cada 5 segundos
  useEffect(() => {
    if (!autenticado) return;
    const interval = setInterval(() => {
      loadCodigos();
      setTempoRestante(30 - (Math.floor(Date.now() / 1000) % 30));
    }, 5000);
    return () => clearInterval(interval);
  }, [autenticado]);

  const handleAtivar2FA = async (usuario) => {
    try {
      const res = await api.post('/auth/2fa/ativar', { user_id: usuario.id });
      if (res.data && res.data.qr_code) {
        setQrCode(res.data.qr_code);
        setSecret(res.data.secret);
        setSelectedUser(usuario);
        setCodigo('');
        toast.success(`2FA ativado para ${usuario.nome}! QR Code gerado.`);
        loadCodigos();
        loadUsuarios();
      } else {
        toast.error('Resposta inesperada do servidor');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar QR Code');
      console.error('Erro 2FA:', error.response?.data || error);
    }
  };

  const handleConfirmar = async () => {
    if (!codigo || codigo.length !== 6) {
      toast.error('Digite o codigo de 6 digitos');
      return;
    }
    setConfirmando(true);
    try {
      await api.post('/auth/2fa/confirmar', { user_id: selectedUser.id, codigo });
      toast.success(`2FA ativado para ${selectedUser.nome}!`);
      setQrCode('');
      setSecret('');
      setSelectedUser(null);
      setCodigo('');
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codigo invalido');
    } finally {
      setConfirmando(false);
    }
  };

  const handleDesativar = async (usuario) => {
    if (!window.confirm(`Desativar 2FA de ${usuario.nome}?`)) return;
    try {
      await api.post('/auth/2fa/desativar', { user_id: usuario.id });
      toast.success(`2FA desativado para ${usuario.nome}`);
      loadUsuarios();
    } catch {
      toast.error('Erro ao desativar');
    }
  };

  // Tela de login
  if (!autenticado) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <Lock className="h-10 w-10 text-purple-600 mx-auto mb-2" />
            <CardTitle>Acesso - 2FA</CardTitle>
            <p className="text-sm text-gray-500">Digite suas credenciais</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@..." className="mt-1" />
              </div>
              <div>
                <Label>Senha</Label>
                <Input type="password" value={senha} onChange={e => setSenha(e.target.value)} className="mt-1" />
              </div>
              {requires2fa && (
                <div>
                  <Label>Codigo Google Authenticator</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    className="mt-1 text-center tracking-widest font-mono text-lg"
                    autoFocus
                  />
                </div>
              )}
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={loadingLogin}>
                {loadingLogin ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Entrar
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Painel 2FA
  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-4">
          <h1 className="text-3xl font-black text-white flex items-center justify-center gap-3">
            <ShieldCheck className="h-8 w-8 text-purple-400" />
            Gerenciamento 2FA
          </h1>
          <p className="text-gray-400 mt-1">Google Authenticator - Multi Telecom</p>
        </div>

        {/* Abas */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setAbaAtiva('tokens')}
            className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all ${abaAtiva === 'tokens' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
          >
            Tokens ao Vivo
          </button>
          {isAdmin && (
            <button
              onClick={() => setAbaAtiva('ativacao')}
              className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all ${abaAtiva === 'ativacao' ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            >
              Ativar / Desativar 2FA
            </button>
          )}
        </div>

        {/* ABA TOKENS */}
        {abaAtiva === 'tokens' && (
          codigos.length > 0 ? (
            <Card className="border-green-800 bg-gray-900">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-400">
                  <ShieldCheck className="h-5 w-5" />
                  Codigos ao Vivo ({tempoRestante}s)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {codigos.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-semibold text-white">{c.nome}</p>
                        <p className="text-xs text-gray-400">{c.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-2xl font-black text-green-400 tracking-widest">{c.codigo_atual}</p>
                        <div className="w-20 bg-gray-700 rounded-full h-1 mt-1">
                          <div className="bg-green-400 h-1 rounded-full transition-all" style={{ width: `${(tempoRestante / 30) * 100}%` }}></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="py-16 text-center">
                <ShieldOff className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Nenhum usuario com 2FA ativo</p>
                <p className="text-gray-500 text-sm mt-1">Ative na aba "Ativar / Desativar 2FA"</p>
              </CardContent>
            </Card>
          )
        )}

        {/* ABA ATIVAÇÃO */}
        {abaAtiva === 'ativacao' && (
          <>
            {selectedUser && qrCode && (
              <Card className="border-purple-300 bg-purple-50 mb-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-800">
                    <ShieldCheck className="h-5 w-5" />
                    2FA Ativado: {selectedUser.nome}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-purple-700">Vendedor deve escanear o QR Code no Google Authenticator:</p>
                  <div className="flex justify-center">
                    <div className="p-4 bg-white rounded-xl border-2 border-purple-200 shadow-lg">
                      <img src={qrCode} alt="QR Code" className="w-52 h-52" />
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg text-center">
                    <p className="text-xs text-gray-500">Codigo manual:</p>
                    <p className="font-mono font-bold tracking-wider text-lg select-all">{secret}</p>
                  </div>
                  <Button variant="outline" onClick={() => { setSelectedUser(null); setQrCode(''); setSecret(''); }}>
                    Fechar
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Usuarios ({usuarios.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
                ) : (
                  <div className="space-y-2">
                    {usuarios.map(u => (
                      <div key={u.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          {u.totp_ativo ? <ShieldCheck className="h-5 w-5 text-green-600" /> : <ShieldOff className="h-5 w-5 text-gray-300" />}
                          <div>
                            <p className="font-semibold">{u.nome}</p>
                            <p className="text-xs text-gray-500">{u.email} - {u.perfil}</p>
                          </div>
                        </div>
                        {u.totp_ativo ? (
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleDesativar(u)}>
                            <ShieldOff className="mr-1 h-4 w-4" /> Desativar
                          </Button>
                        ) : (
                          <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => handleAtivar2FA(u)}>
                            <ShieldCheck className="mr-1 h-4 w-4" /> Ativar 2FA
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Admin2FA;
